import * as React from 'react'
import { HeadFC } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { Breadcrumbs, JumpLinkLists } from '@type/component-data-types'
import Layout from '@components/_Layout/Layout'
import MetaElements from '@components/head/MetaElements/MetaElements'
import ClientCta from '@components/ClientCta/ClientCta'
import generateBreadCrumb from '@utility/generateBreadCrumb'
import JumpLinks from '@components/JumpLinks/JumpLinks'
import Heading from '@components/heading/Heading'
import Article from '@components/article/Article'
import ArticleRead from '@components/article/ArticleRead'
import ArticleBody from '@components/article/ArticleBody'
import Box from '@components/Box/Box'
import InterviewQa from '@components/advertiser/InterviewQa/InterviewQa'
import ExternalAfbTopLink from '@components/link/ExternalAfbTopLink/ExternalAfbTopLink'

const breadcrumb: Breadcrumbs = generateBreadCrumb([
  {
    name: 'クライアントtop',
    item: '/advertiser/',
    position: '2',
  },
  {
    name: 'インタビュー',
    item: '/advertiser/interview/',
    position: '3',
  },
  {
    name: 'LINEヤフー様',
    item: '/advertiser/interview/case1/',
    position: '4',
  },
])

const jumpLinkItems: JumpLinkLists = [
  {
    section:
      '有力企業へのリーチとGMV拡大が最優先。工数対効果を重視した運営体制と課題',
    href: '#header_section_1',
  },
  {
    section:
      '海外案件への強みと「攻め」の提案力が決め手。既存ASPにはない独自案件の魅力',
    href: '#header_section_2',
  },
  {
    section:
      '全体GMVの一翼を担う存在へ。精度の高い事前調整がもたらす円滑な運用成果',
    href: '#header_section_3',
  },
  {
    section: '未踏領域の開拓を共に。より強固なパートナーシップで目指す事業拡大',
    href: '#header_section_4',
  },
]

const MODIFIED_DATE = '2026/04/20'
const PUBLISH_DATE = '2026/04/20'

const Case1 = (): React.JSX.Element => {
  return (
    <>
      {/* ページ本体 */}
      <Layout
        isHeaderType="client-no-form"
        isFooterType="client"
        isSideBarType="client"
        pageType="client-no-side-bar"
        breadcrumb={breadcrumb}
      >
        <Article>
          <Heading tagName="h1">
            流通取引総額の一躍を担う。afbが支えるポイント事業の成長戦略
          </Heading>
          <ArticleRead className="mb-3">
            <p>
              LINEヤフー株式会社 様
              <br />
              サイト：
              <ExternalAfbTopLink to="https://ec.line.me/" type="inline">
                https://ec.line.me/
              </ExternalAfbTopLink>
              【LINEブランドカタログ】
            </p>
          </ArticleRead>
          {/* FV坓像 */}
          <StaticImage
            src="../../../assets/images/advertiser/interview/case1/client_interview_line_visual_01.webp"
            alt="LINEヤフー株式会社 LINEブランドカタログ"
            placeholder="blurred"
            width={930}
            className="mb-6"
          />

          {/* 導入文 */}
          <ArticleRead className="mb-7">
            <p>
              LINEヤフー株式会社が運営する「LINEブランドカタログ」は、買い物体験を通じたポイント還元を提供する、国内有数のポイントプラットフォームです。同サービスでは、流通取引総額（GMV）の拡大を進める中で、既存のASPでは十分にリーチできない有力広告主の開拓や、注力領域である越境EC分野へのアプローチに課題を抱えていました。
            </p>
            <p>
              そうした状況を打開するため、海外案件に強みを持つ「afb」を導入したところ、現在では全体GMVの数％を「afb」経由の案件が占めるまでに成長し、事業を支える重要な収益源となっています。
            </p>
            <p>
              本記事では、「afb」との連携によって得られた具体的な成果に加え、三社会議を通じて実現したスムーズな意思決定や調整のプロセスについて、ご担当者様に詳しく伺いました。
            </p>

            {/* 導入後の効果ボックス */}
            <Box className="mb-3">
              <p className="fw-bold">＜導入後の効果＞</p>
              <ul className="list-unstyled mb-0">
                <li className="fw-bold">
                  ・流通取引総額（GMV）の数％をafb経由の案件が占める規模まで成長
                </li>
                <li className="fw-bold">
                  ・afb独自の海外案件や越境EC領域へのリーチにより、商材の差別化を実現
                </li>
                <li className="fw-bold">
                  ・密なコミュニケーションと事前調整により、広告主との円滑な合意形成が可能に
                </li>
              </ul>
            </Box>
          </ArticleRead>

          {/* 目次 */}
          <div className="mb-7">
            <JumpLinks linkItem={jumpLinkItems} isStickyEnabled={true} />
          </div>

          {/* セクション1 */}
          <Article type="children">
            <Heading tagName="h2" id="header_section_1">
              有力企業へのリーチとGMV拡大が最優先。工数対効果を重視した運営体制と課題
            </Heading>
            <StaticImage
              src="../../../assets/images/advertiser/interview/case1/client_interview_line_visual_02.webp"
              alt="有力企業へのリーチとGMV拡大が最優先。工数対効果を重視した運営体制と課題"
              placeholder="blurred"
              width={930}
              className="mb-5"
            />
            <ArticleBody>
              <InterviewQa question="LINEブランドカタログのビジネスモデルと、その中でのアフィリエイトの立ち位置を教えてください。">
                <p>
                  <span className="interviewQa__answerLabel">
                    LINEブランドカタログ担当者様（以下ご担当者様）：
                  </span>
                  <br />
                  LINEブランドカタログは、いわゆる「ポイ活」を楽しめるポイントサイトです。ユーザーが本媒体を経由して買い物をすると、発生したアフィリエイト報酬をポイントとして還元する仕組みになっています。収益の中心は、ASP経由で広告主から受領するアフィリエイトフィーで、ビジネスモデルを支える主軸といえます。現在は数名体制で複数のASPを担当しており、少数精鋭で高い密度の運用を回している状況です。
                </p>
              </InterviewQa>
              <InterviewQa question="事業を運営する上で、どのような指標をKPIとして追っているのでしょうか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  最も重視しているのは流通取引総額（以下、GMV）です。売上をKPIに置くケースも多いと思いますが、当社では期ごとの優先度や全社方針を踏まえると、まずGMVを伸ばすことがアフィリエイト収益の最大化に直結すると捉えています。加えて、LINEならではの指標として、LINE公式アカウントの友だち数などプラットフォームの広がりを示す数値も見ています。複数の指標を組み合わせながら、成長を多面的に管理しているイメージですね。
                </p>
              </InterviewQa>
              <InterviewQa question="「afb」導入前に抱えていた、広告主開拓における課題を教えてください。">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  特定のECモールや、出店いただきたい有力企業へのリーチ手段を常に探していました。当社の媒体特性上、広告主と連携する際は必ずASPを介する仕組みです。つまり、掲載したいショップを取り扱っているASPを起点に、新たな連携先を検討する必要がありました。常に20〜30社ほど「ぜひ掲載したい広告主」をリストアップし、接続可能な最適ルートを探し続けていた形です。
                </p>
              </InterviewQa>
              <InterviewQa question="新しいASPとの連携を検討する際、どのような基準で判断されているのでしょうか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  追加の運用工数に見合う費用対効果があるかを最優先に見ています。現在は10社程度のASPと連携しており、国内の大手企業とは概ね接続できている状態です。一方で、中小規模のASPからお声がけいただくこともありますが、リソースには限りがあります。既存の接続先ではカバーできない独自案件があるか、あるいは工数をかけても新たに繋ぐ価値があるかを厳しく精査しています。
                  <br />
                  その結果として、お断りするケースも少なくありません。
                </p>
              </InterviewQa>
            </ArticleBody>
          </Article>

          {/* セクション2 */}
          <Article type="children">
            <Heading tagName="h2" id="header_section_2" className="mb-5">
              海外案件への強みと「攻め」の提案力が決め手。既存ASPにはない独自案件の魅力
            </Heading>
            <ArticleBody>
              <InterviewQa question="数あるASPの中で、「afb」の導入を決定した理由を教えてください。">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  大きな理由は、当社が接続を希望していた有力企業をご提案いただけた点と、海外案件や越境EC領域に強みを持っていた点です。国内の大手企業については、すでに他のASP経由で一定の連携が進んでいました。一方で、当時は越境ECなど新たな成長領域への展開を強化したいフェーズでもあり、そうした領域に対して具体的な選択肢を提示していただけたことは非常に魅力的でした。社内で検討を重ねた結果、「afb」と連携することで商材ラインナップや媒体としての座組がより強固になると判断し、導入を決めています。
                </p>
              </InterviewQa>
              <InterviewQa question="「afb」の提案内容や、担当者のコミュニケーションにおいて特徴的な点はありますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  こちらから要望を出す前に、当社がどのような広告主を求めているのかを汲み取った上で提案いただける点が印象的です。ASPによっては、媒体側が常に働きかけなければ案件が進まないケースもありますが、「afb」は状況を理解したうえで主体的にご紹介いただけます。月によっては、1桁後半から10件前後の広告主をご提案いただくこともありました。ビジネス規模やセグメントを踏まえた案件が揃っており、提案の質という点でも、他社と比較して際立っていると感じています。
                </p>
              </InterviewQa>
              <InterviewQa question="「afb」独自の案件については、どのような印象をお持ちでしょうか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  他社では取り扱っていない、「『afb』ならでは」の独自案件が多い点は、媒体運営の観点から非常に助かっています。独自案件であるがゆえに、規約の言語対応など特殊な条件が付くこともありますが、それ以上に代替の利かない商材を扱えるメリットは大きいですね。他媒体との差別化を図るうえでも、「afb」が独自のリレーションを通じて開拓された案件は価値があります。こうした魅力的な商材を継続的に提供いただいていることが、取り扱い件数の着実な拡大につながっていると考えています。
                </p>
              </InterviewQa>
            </ArticleBody>
          </Article>

          {/* セクション3 */}
          <Article type="children">
            <Heading tagName="h2" id="header_section_3" className="mb-5">
              全体GMVの一翼を担う存在へ。精度の高い事前調整がもたらす円滑な運用成果
            </Heading>
            <ArticleBody>
              <InterviewQa question="「afb」の導入後、定量的にどのような成果が出ていますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  LINEブランドカタログ全体のGMVにおいて、「afb」経由の案件が数％～を占めるまでに成長しています。これは当社にとって大きな成果であり、現在では事業を支える収益の一翼を担う存在です。とりわけ、他社では取り扱いのない独自案件や、当時注力していた越境EC領域の商材が大きく寄与しています。代替の利かないラインナップが揃っていることが、安定的なGMV拡大につながっていると感じています。
                </p>
              </InterviewQa>
              <InterviewQa question="「afb」の提案内容や、担当者のコミュニケーションにおいて特徴的な点はありますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  当社、広告主、「afb」の三者で協議を行う際に、事前の調整を非常に丁寧に行っていただける点が印象的です。会議前の段階で、広告主が置かれている状況や懸念点を詳細に共有してくださるため、当日は論点を整理したうえで具体的な提案に踏み込めます。情報の齟齬がない状態で議論をスタートできるため、結論を先送りすることなく、スピーディーかつ納得感のある合意形成が実現できているのでいつも助かっています。
                </p>
              </InterviewQa>
              <InterviewQa question="日々のコミュニケーションや、対応スピードについてはどのように評価されていますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  対応は非常に柔軟で、スピード感も申し分ありません。アフィリエイト業界ではメールでのやり取りが中心になりがちですが、「afb」は緊急性の高い案件については電話でも即座に対応してくださいます。特に、調整事項が多い越境EC案件では、迅速に動いていただける点が大きな助けになっています。また、単なる仲介にとどまらず、広告主の背景や意図を踏まえたうえで次の打ち手を一緒に検討できる点も、運用を円滑に進める上での強みだと感じています。
                </p>
              </InterviewQa>
            </ArticleBody>
          </Article>

          {/* セクション4 */}
          <Article type="children">
            <Heading tagName="h2" id="header_section_4">
              未踏領域の開拓を共に。より強固なパートナーシップで目指す事業拡大
            </Heading>
            <StaticImage
              src="../../../assets/images/advertiser/interview/case1/client_interview_line_visual_03.webp"
              alt="未踏領域の開拓を共に。より強固なパートナーシップで目指す事業拡大"
              placeholder="blurred"
              width={930}
              className="mb-5"
            />
            <ArticleBody>
              <InterviewQa question="今後、「afb」をどのように活用し、どのような成果を上げていきたいと考えていますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  LINEブランドカタログとしては、今後さらに取り扱い商材のカテゴリーを拡張していく方針を検討しています。その過程では、これまで当社が十分な知見を持っていなかった「未踏領域」の商材において、信頼できる広告主との新たな接点をつくる必要があります。「afb」は、当社の意図やフェーズを踏まえたうえで、タイムリーかつ的確な提案を行っていただける点が強みです。新領域への展開を進める局面において、より多角的で競争力のある案件をご提案いただけることを期待しています。
                </p>
              </InterviewQa>
              <InterviewQa question="最後に、どのような課題を持つ企業に「afb」を勧めたいと感じますか？">
                <p>
                  <span className="interviewQa__answerLabel">ご担当者様：</span>
                  <br />
                  単純に売上拡大を目指すだけでなく、「どのようなユーザーに、どのように届けたいか」といった点まで重視している企業に適したASPだと思います。広告主側の細かな要望を汲み取り、媒体との間で最適な条件設計や調整を行ってくれる点は、「afb」ならではの価値です。また、媒体の立場から見ても、海外案件に強みを持ち、担当者が相談しやすい距離感で伴走してくれる点は大きな安心材料です。対外的な折衝を任せるパートナーとして、信頼できる存在だと感じています。
                </p>
              </InterviewQa>
            </ArticleBody>
          </Article>

          <p className="mb-5">
            <small>※掲載中の画像は全てイメージです</small>
          </p>

          <ClientCta className="mb-3" />
        </Article>
      </Layout>
    </>
  )
}

export default Case1

export const Head: HeadFC = () => (
  <MetaElements
    pageTitle="流通取引総額の一躍を担う。afbが支えるポイント事業の成長戦略｜LINEヤフー株式会社"
    description="LINEヤフー株式会社が運営するLINEブランドカタログにおいて、afbの導入により全体GMVの数％を占める規模まで成長した事例をご紹介します。海外案件への強みや三社会議での連携など、具体的な成果と活用ポイントをご担当者様に伺いました。"
    breadcrumbs={breadcrumb}
    publishDate={PUBLISH_DATE}
    modifiedDate={MODIFIED_DATE}
    mainEntityOfPage="/advertiser/interview/case1/"
    about="afb導入事例：LINEヤフー株式会社 LINEブランドカタログ"
  />
)
